/**
 * View Config Modal
 * Modal dialog for viewing config details
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import { BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryItem } from '../types/configRegistryTypes';
import { getConfigGlobalValue } from '../utils/templateValueHelpers';

interface ViewConfigModalProps {
  open: boolean;
  onClose: () => void;
  config: ConfigRegistryItem | null;
  templateValue?: string | null;
}

export const ViewConfigModal: React.FC<ViewConfigModalProps> = ({
  open,
  onClose,
  config,
  templateValue,
}) => {
  if (!config) return null;

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value || '-'}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Config Details
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {config.displayName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <InfoRow 
            label="Config Key" 
            value={<code style={{ fontFamily: 'monospace', color: '#1976d2' }}>{config.configKey}</code>} 
          />
          
          <InfoRow label="Display Name" value={config.displayName} />
          
          <InfoRow 
            label="Config Type" 
            value={<Chip label={config.configType} size="small" variant="outlined" />} 
          />
          
          <InfoRow 
            label="Data Type" 
            value={<Chip label={config.configDataType} size="small" color="primary" />} 
          />

          <InfoRow
            label="Template Value"
            value={
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {templateValue || 'Not set'}
              </Box>
            }
          />

          {config.configDocumentation && (
            <>
              <Divider sx={{ my: 2 }} />
              <InfoRow label="Documentation" value={config.configDocumentation} />
            </>
          )}

          {getConfigGlobalValue(config) && (
            <>
              <Divider sx={{ my: 2 }} />
              <InfoRow 
                label="GLOBAL Template Value" 
                value={
                  <Box 
                    sx={{ 
                      p: 1, 
                      bgcolor: 'grey.100', 
                      borderRadius: 1, 
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {getConfigGlobalValue(config)}
                  </Box>
                } 
              />
            </>
          )}

          {config.possibleValues && (
            <>
              <Divider sx={{ my: 2 }} />
              <InfoRow
                label="Possible Values"
                value={
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(config.possibleValues, null, 2)}
                  </Box>
                }
              />
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <InfoRow 
              label="Mandatory" 
              value={config.isMandatoryField ? '✓ Yes' : '✗ No'} 
            />
            <InfoRow 
              label="Deprecated" 
              value={config.deprecated ? '⚠️ Yes' : '✓ No'} 
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={onClose}>
          Close
        </BglSecondarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

